"use client";

import { track } from "@vercel/analytics";
import { IconSquareBehindSquare1 } from "central-icons-outlined/IconSquareBehindSquare1";
import type { ComponentProps } from "react";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { IconButton } from "@/components/ui/icon-button";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import CustomCheckmarkIcon from "@/icons/custom-checkmark-icon";
import { cn } from "@/lib/utils";

type CopyIconButtonProps = Omit<
  ComponentProps<typeof IconButton>,
  "aria-label" | "children" | "onClick"
> & {
  copiedLabel?: string;
  event?: { name: string; properties?: Record<string, string> };
  iconClassName?: string;
  label: string;
  text: string;
};

export function CopyIconButton({
  copiedLabel = "Copied",
  event,
  iconClassName,
  label,
  size = "sm",
  text,
  variant = "ghost",
  ...props
}: CopyIconButtonProps) {
  const { isCopied, copy } = useCopyToClipboard();

  return (
    <IconButton
      aria-label={label}
      onClick={() => {
        copy(text);
        if (event) {
          track(event.name, event.properties);
        }
      }}
      size={size}
      type="button"
      variant={variant}
      {...props}
    >
      <AnimatedIcon
        active={isCopied}
        activeIcon={
          <CustomCheckmarkIcon
            className={cn(
              "size-4.5 text-gray-1000 will-change-transform",
              iconClassName
            )}
          />
        }
        idleIcon={
          <IconSquareBehindSquare1
            className={cn(
              "size-4.5 text-gray-1000 transition-colors duration-200 ease-out will-change-transform group-hover:text-gray-1200",
              iconClassName
            )}
          />
        }
      />
      <span className="sr-only" role="status">
        {isCopied ? copiedLabel : ""}
      </span>
    </IconButton>
  );
}
